/**
 * RedwoodScrapyard — Level 1.
 *
 * Giant redwoods strangled by rusted motorcycle parts.
 * Theme: Overcoming Inertia.
 *
 * Puzzles (in order) — each is a self-contained `PuzzleModule` under
 * `../levels/puzzles/redwood-scrapyard/`:
 *   1. The Seized Crane      — `SeizedCranePuzzle`
 *   2. The Crow vs. Squirrel — `CrowSquirrelPuzzle`
 *   3. The Kitten Corral     — `KittenCorralPuzzle`
 *   4. The Hoodie Papoose    — `HoodieJigsawPuzzle` (apex)
 *   5. Final Blockage        — `FinalBlockagePuzzle` (gates on 1-4)
 *
 * This file is now the scene shell only: boot, camera, lighting, post-
 * processing, environment art, the Elder Kitty encounter, Resonant Drop
 * unlock, and level completion. All puzzle geometry + logic lives in
 * the puzzle modules.
 *
 * Elder Kitty's cutscene is triggered by `BLOCKAGE_SHATTERED` — the co-op
 * ability reward is taught during that cutscene and the exit portal
 * opens at the end.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import { applyVisualProfile, REDWOOD_SCRAPYARD_PROFILE } from "../levels";
import { getOrMakeNoiseBump } from "../systems/ProceduralNoise";

import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";

import { getAssetManager } from "../systems/AssetManager";
import {
  freezeStaticMeshes,
  DEFAULT_STATIC_EXCLUDES,
} from "../levels/staticOptimization";
import { tagAs } from "../levels";
import { PhysicsSystem } from "../systems/PhysicsSystem";
import { CameraSystem } from "../systems/CameraSystem";
import { InputManager } from "../systems/InputManager";
import { AbilityHolder } from "../systems/AbilitySystem";
import { AudioManager } from "../systems/AudioManager";
import { CutsceneSystem, CutsceneStep } from "../systems/CutsceneSystem";
import { ELDER_KITTY_ENCOUNTER, LEVEL_INTRO, controlHintForMode } from "../config/dialogue";

import { Portal } from "../entities/interactives/Portal";
import { GreaseCache } from "../entities/interactives/GreaseCache";
import { PapooseCarrier } from "../entities/interactives/PapooseCarrier";
import { ElderKitty } from "../entities/npcs/ElderKitty";

import { InteractiveInfo } from "../abilities/minna/AuraSight";
import { ShadowZoneInfo } from "../abilities/minna/ShadowSlip";
import { ResonantDrop } from "../abilities/coop/ResonantDrop";
import { LevelCueDriver } from "../levels/LevelCueDriver";

import { SCRAPYARD, COLORS, WORLD_DEBUG } from "../config/GameConfig";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";

import { setupBaseLevel } from "./BaseLevel";
import { tryCoopAbility } from "./coopAbility";
import { setupElderBiscuitAnchor } from "./setupElderBiscuitAnchor";

import { REDWOOD_SCRAPYARD_PLATFORMS, platformDepth, platformHeight } from "../levels/layouts";
import type { PuzzleContext, PuzzleModule, PuzzleState } from "../levels/PuzzleModule";
import { SeizedCranePuzzle } from "../levels/puzzles/redwood-scrapyard/SeizedCrane";
import { CrowSquirrelPuzzle } from "../levels/puzzles/redwood-scrapyard/CrowSquirrel";
import { KittenCorralPuzzle } from "../levels/puzzles/redwood-scrapyard/KittenCorral";
import { HoodieJigsawPuzzle } from "../levels/puzzles/redwood-scrapyard/HoodieJigsaw";
import { FinalBlockagePuzzle } from "../levels/puzzles/redwood-scrapyard/FinalBlockage";
import { applyScrapyardCatLighting } from "../levels/art/ScrapyardCatLighting";

// ─── Types ──────────────────────────────────────────────────────────

export interface RedwoodScrapyardContext {
  physicsSystem: PhysicsSystem;
  cameraSystem: CameraSystem;
  inputManager: InputManager;
  minna: import("../entities/players/Minna").Minna;
  basil: import("../entities/players/Basil").Basil;
  minnaAbilities: AbilityHolder;
  basilAbilities: AbilityHolder;
  audioManager: AudioManager;
  cutsceneSystem: CutsceneSystem;
}

// ─── Main Setup ─────────────────────────────────────────────────────

/**
 * Set up the Redwood Scrapyard scene.
 * Call after the engine and scene are created and physics is initialized.
 */
export async function setupRedwoodScrapyard(scene: Scene): Promise<RedwoodScrapyardContext> {
  // --- Base level setup ---
  const base = await setupBaseLevel(scene, {
    levelKey: "RedwoodScrapyard",
    clearColor: new Color4(0.25, 0.34, 0.28, 1.0),
    fogColor: new Color3(0.30, 0.39, 0.32),
    fogDensity: 0.009,
    // Spawn staggered around p1_1's center (x=7, width=10, spans
    // x∈[2, 12]). Prior spawn at x=5 sat 3u from the west edge — a
    // single reflex `d`-tap (screen-right = world-negative-X) walked
    // the cat off in <1s. New spawn at x=6/8 has ~4u margin both
    // sides. Same pattern as the L3 spawn fix.
    minnaSpawn: new Vector3(6, 4, 0),
    basilSpawn: new Vector3(8, 4, 0),
    auraSightVisualConfig: { glowIntensity: 0.32, threadRadius: 0.012, threadBrightness: 0.4 },
    calmingResonanceVisualConfig: {
      ringThickness: 0.008, ringOpacity: 0.15, ringPlane: "ground", ringHeightOffset: -0.67,
    },
    turnTableTouchConfig: { visualRadius: 0.85, visualDepthOffset: 0.8 },
    abilityVfxVisualConfig: { particleDensityScale: .25, particleSizeScale: .65 },
    resonanceStreamOpacity: 0,
    verticalFraming: { paddingAbove: 2.7, paddingBelow: 0.8, topInset: 0.25, bottomInset: 0.12 },
  });

  const {
    physics: physicsSystem, minna, basil, minnaAbilities, basilAbilities,
    auraSight, shadowSlip, calmingResonance, turnTableTouch, pneumaticAnchor,
    camera: cameraSystem, audioManager, cutsceneSystem, inputManager,
  } = base;

  // --- Visual profile: lights, shadows, post-process, glow, fog, clearColor ---
  const visualHandles = applyVisualProfile(scene, REDWOOD_SCRAPYARD_PROFILE, {
    glowLayer: base.getGlowLayer(),
  });
  base.addDisposeCallback(() => visualHandles.dispose());

  const glowLayer = visualHandles.glowLayer;
  const shadowGen = visualHandles.shadowGenerator!;
  base.shadowGenerator = shadowGen;

  // The authored forest owns all visible walking surfaces. Physics remains
  // the authoritative layout; keep its visuals if the model cannot load.
  const atmosphereMotesPS = createAtmosphereMotes(scene);
  cameraSystem.registerAmbientParticles([atmosphereMotesPS]);
  const shadowZones = createShadowZones(scene);
  const { ground, platforms } = createGroundAndPlatforms(scene, physicsSystem, shadowGen);
  try {
    const forest = await getAssetManager().instantiateModel(
      scene, "/assets/models/environment/scrapyard_forest.glb",
    );
    for (const mesh of forest.meshes) {
      mesh.isPickable = false;
      mesh.receiveShadows = true;
      // Only the actual decks need to cast in the playable corridor.
      if (mesh.name.includes("DeckTop_")) shadowGen.addShadowCaster(mesh);
    }
    if (ground) ground.isVisible = false;
    for (const platform of platforms) {
      platform.isVisible = false;
      shadowGen.removeShadowCaster(platform);
    }
    freezeStaticMeshes(forest.meshes, { excludePatterns: DEFAULT_STATIC_EXCLUDES });
    base.addDisposeCallback(() => forest.dispose());
  } catch (err) {
    console.warn("[RedwoodScrapyard] Forest unavailable; keeping visible collision surfaces:", err);
  }

  // A capsule registered before its asynchronous skin arrives casts no cat.
  await Promise.all([minna.modelReady, basil.modelReady]);
  base.addDisposeCallback(applyScrapyardCatLighting(minna.mesh, basil.mesh));
  shadowGen.addShadowCaster(minna.mesh);
  shadowGen.addShadowCaster(basil.mesh);

  // Keep a useful shadow volume around the shipping view instead of fitting
  // a 154-unit forest. This is presentation only, never simulation logic.
  const key = visualHandles.keyLight;
  key.shadowMinZ = 0.1;
  key.shadowMaxZ = 80;
  key.shadowFrustumSize = 32;
  const updateShadowPosition = (): void => {
    const x = Math.round(cameraSystem.camera.position.x * 64) / 64;
    key.position.set(x - 5.4, 24, 10.8);
  };
  updateShadowPosition();
  base.addDisposeCallback(base.registerSafeBeforeRender("scrapyardShadowView", updateShadowPosition));

  // --- Co-op ability (available from level start; Resonant Drop is taught
  //     in the Elder Kitty cutscene — the ability object lives throughout) ---
  const resonantDrop = new ResonantDrop(scene, basil.mesh, minna.mesh);
  basilAbilities.addAbility(resonantDrop);

  // --- Shadow zones ---
  shadowSlip.registerShadowZones(shadowZones);

  // ─── Puzzle Modules ──────────────────────────────────────────────
  //
  // Section layout (world X positions):
  //   Section 1:   0-30  (Puzzle 1 - Seized Crane)
  //   Section 2:  30-70  (Puzzle 2 - Crow vs Squirrel)
  //   Section 3:  70-110 (Puzzle 3 - Kitten Corral)
  //   Section 4: 110-135 (Puzzle 4 - Apex Jigsaw)
  //   Section 5: 135-154 (Puzzle 5 - Final Blockage + Elder)

  const seizedCrane = new SeizedCranePuzzle();
  const crowSquirrel = new CrowSquirrelPuzzle();
  const kittenCorral = new KittenCorralPuzzle();
  const hoodieJigsaw = new HoodieJigsawPuzzle();
  const finalBlockage = new FinalBlockagePuzzle();

  const puzzles: PuzzleModule[] = [
    seizedCrane,
    crowSquirrel,
    kittenCorral,
    hoodieJigsaw,
    finalBlockage,
  ];

  // Level-scoped shared puzzle state (read by finalBlockage to gate on
  // the other four). Each puzzle writes into this map each frame via the
  // game-loop wrapper below.
  const levelState = new Map<string, PuzzleState>();

  // AuraSight interactive collection — puzzles contribute their meshes
  // here via `ctx.registerInteractive`, then we pass the full list to the
  // ability once setup is complete and add the scene-level connections.
  const allInteractives: InteractiveInfo[] = [];

  // Hoodie Papoose — created up front so both the apex jigsaw and the
  // kitten corral can share a single carrier via the puzzle context.
  // Activation is deferred until the Jigsaw solves (canon book p.13):
  // the carrier starts inactive and `activate()` fires on solve.
  const papooseCarrier = new PapooseCarrier(scene, minna.mesh);
  base.addDisposeCallback(() => papooseCarrier.dispose());

  const puzzleCtx: PuzzleContext = {
    scene,
    physicsSystem,
    glowLayer,
    shadowGenerator: shadowGen,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    auraSight,
    shadowSlip,
    calmingResonance,
    turnTableTouch,
    pneumaticAnchor,
    audioManager,
    cutsceneSystem,
    gameEvents,
    levelState,
    registerInteractive: (info) => {
      allInteractives.push(info);
    },
    addUnsub: (unsub) => base.addUnsub(unsub),
    papooseCarrier,
  };

  // Setup all puzzles.
  for (const puzzle of puzzles) {
    await puzzle.setup(puzzleCtx);
    levelState.set(puzzle.id, puzzle.getState());
  }

  // Material state feedback belongs to each interactive. Broad emission on
  // every child washed out the lever/plate faces and erased their notches.

  // Expose loaded puzzle IDs for runtime parity testing against the
  // level's MechanicContract (see tests/integration/puzzle-id-parity.spec.ts).
  scene.metadata = {
    ...(scene.metadata ?? {}),
    loadedPuzzleIds: puzzles.map((p) => p.id),
  };

  // Composite puzzle handle for QA tooling parity with single-puzzle
  // levels (Level 0). get_puzzle_state returns each module's live state
  // keyed by puzzle id; get_puzzle_internals exposes the live module
  // refs so fix-verify loops can call dial.spin / jigsaw.cycleSlot / etc.
  const compositePuzzle = {
    id: "redwoodComposite",
    getState: () => ({
      puzzles: Object.fromEntries(puzzles.map((p) => [p.id, p.getState()])),
    }),
    getInternals: () => ({
      seizedCrane,
      crowSquirrel,
      kittenCorral,
      hoodieJigsaw,
      finalBlockage,
    }),
  };
  const levelHandleMeta = scene.metadata?.level as
    | {
        setPuzzle?: (p: unknown) => void;
        registerPuzzles?: (puzzles: PuzzleModule[], levelState: Map<string, PuzzleState>) => void;
      }
    | undefined;
  levelHandleMeta?.setPuzzle?.(compositePuzzle);
  // Expose per-puzzle modules to the QA harness via getQAHints() — see
  // OvergrownConservatory for the motivating pattern. Also unlocks
  // forceSolvePuzzle + __startAtPuzzle deterministic fast-forwards.
  levelHandleMeta?.registerPuzzles?.(puzzles, levelState);

  // --- Hero scrap: Honda CB175 motorcycle crashed into the apex grove ---
  // Photogrammetry scan sourced via Sketchfab MCP. Sits half-buried in
  // front of the final blockage so the apex beat reads as "motorcycle
  // scrap strangling the redwoods" per the concept art. Loaded inline
  // (awaited) so HMR cannot lose the reference to a stale module.
  //
  // Phase C4 densified: the single hero bike read as a lonely set-piece.
  // Concept art p.12 shows motorcycles wedged between every pair of
  // trunks, so we now scatter CB175 instances at four deep-z positions
  // plus the hero position. Shared GLB is instanced, not re-loaded.
  try {
    const heroBike = await getAssetManager().instantiateModel(
      scene,
      "/assets/models/environment/hero/motorcycle_cb175.glb",
    );
    // The raw scan is ~22 units tall; scale 0.07 → ~1.5 m bike height.
    for (const root of heroBike.rootNodes) {
      root.position.set(138, 0.3, 0);
      root.scaling.setAll(0.07);
      root.rotation.y = Math.PI * 0.35;
      root.rotation.z = 0.18; // tipped over
    }
    for (const mesh of heroBike.meshes) {
      mesh.isPickable = false;
      shadowGen.addShadowCaster(mesh);
      mesh.receiveShadows = true;
    }
    base.addDisposeCallback(() => heroBike.dispose());

    // Background wrecks — half-buried at z≈-4.5 so cats walk past them.
    // Scale slightly smaller (0.055–0.065) so they read as "more scrap"
    // rather than competing with the hero bike for focus.
    const WRECK_POSITIONS: Array<{ x: number; y: number; z: number; yaw: number; roll: number; scale: number }> = [
      { x: 20,  y: 0.3, z: -4.5, yaw: 0.6,  roll: 0.45, scale: 0.06  },
      { x: 52,  y: 0.3, z: -4.2, yaw: -1.2, roll: 0.72, scale: 0.055 },
      { x: 78,  y: 0.3, z: -4.8, yaw: 0.9,  roll: -0.5, scale: 0.065 },
      { x: 104, y: 0.3, z: -4.3, yaw: -0.5, roll: 0.3,  scale: 0.06  },
    ];
    for (let i = 0; i < WRECK_POSITIONS.length; i++) {
      const p = WRECK_POSITIONS[i];
      const wreck = await getAssetManager().instantiateModel(
        scene,
        "/assets/models/environment/hero/motorcycle_cb175.glb",
      );
      for (const root of wreck.rootNodes) {
        root.position.set(p.x, p.y, p.z);
        root.scaling.setAll(p.scale);
        root.rotation.y = p.yaw;
        root.rotation.z = p.roll;
      }
      for (const mesh of wreck.meshes) {
        mesh.isPickable = false;
        shadowGen.addShadowCaster(mesh);
        mesh.receiveShadows = true;
      }
      freezeStaticMeshes(wreck.meshes, { excludePatterns: DEFAULT_STATIC_EXCLUDES });
      base.addDisposeCallback(() => wreck.dispose());
    }
  } catch (err) {
    console.warn("[RedwoodScrapyard] hero motorcycle failed to load:", err);
  }

  // --- Elder Kitty (hidden until the encounter cutscene reveals him) ---
  const elderDeck = REDWOOD_SCRAPYARD_PLATFORMS.platforms.find(p => p.name === "p5_2")!;
  const elderKittyPos = new Vector3(elderDeck.x, elderDeck.y + (elderDeck.h ?? .4) / 2 + .8, 0);
  const elderKitty = new ElderKitty(scene, elderKittyPos, shadowGen);
  elderKitty.mesh.setEnabled(false);
  base.addDisposeCallback(() => elderKitty.dispose());

  allInteractives.push({
    mesh: elderKitty.mesh,
    hidden: false,
    state: "revealed",
  });

  // ── Making Biscuits anchor (Kitty's bedroll) ──
  // Per SPEC-making-biscuits.md §2: "Elder Kitty's leather-and-brass
  // bedroll in a scrapyard alcove." Placed a few units west of the
  // Elder's reveal spot so Minna discovers it during the post-encounter
  // exploration beat. Hidden by default — revealed via Aura Sight.
  const kittyBiscuit = setupElderBiscuitAnchor({
    scene,
    elder: "kitty",
    minna, inputManager,
    cutsceneSystem,
    position: new Vector3(elderKittyPos.x - 4, 0.3, 0.2),
    interactiveRegistry: allInteractives,
  });
  base.addDisposeCallback(() => kittyBiscuit.dispose());

  // ── Pre-encounter destination beacon ──
  //
  // Before Elder Kitty reveals himself the player has no visual goal at
  // the far east of the level — the QA pass flagged this as "no north-star"
  // for the long horizontal traverse. Place a low-intensity cool-dim
  // point light at the Elder's spawn so the player has something to
  // walk toward, without spoiling the reveal. Per canon (book p. 14)
  // Elder Kitty is a "slight black ninja" whose own glow is moonlit,
  // not a campfire — so the beacon matches his palette rather than the
  // rust-and-redwood of the scrapyard around it. That tonal offset is
  // what lets a distant watcher read as "that's the Elder, not more
  // scrap." The cutscene bumps this beacon's intensity during the
  // reveal to sell the starry-void moment.
  const elderBeacon = new PointLight(
    "elderKittyBeacon",
    new Vector3(elderKittyPos.x, elderKittyPos.y + 2.2, elderKittyPos.z),
    scene,
  );
  elderBeacon.diffuse = new Color3(0.45, 0.55, 0.72);
  elderBeacon.specular = new Color3(0.6, 0.7, 0.85);
  elderBeacon.intensity = 0.55;
  elderBeacon.range = 18;
  base.addDisposeCallback(() => elderBeacon.dispose());

  // ── Exit portal (scene-owned; opened by the Elder Kitty cutscene) ──
  const exitPortal = new Portal(scene, {
    key: "scrapyardPortal",
    position: new Vector3(148, 3, 0),
    label: "Portal to Overgrown Conservatory",
    modelPath: "/assets/models/environment/scrapyard_portal.glb",
    brassGlowScale: 0,
    shadowGenerator: shadowGen,
  });
  allInteractives.push({
    mesh: exitPortal.rootMesh,
    hidden: false,
    state: "revealed",
  });

  base.addDisposeCallback(() => exitPortal.dispose());

  // ── Register AuraSight interactives + connection lines ──
  auraSight.registerInteractives(allInteractives);

  // Build connection pairs by mesh name — robust against puzzle ordering.
  function findInfo(rootMeshName: string): InteractiveInfo | null {
    return allInteractives.find((i) => i.mesh.name === rootMeshName) ?? null;
  }
  const plateInfo = findInfo("interactive_cranePlate_root");
  const leverInfo = findInfo("interactive_craneLever_root");

  const connections: Array<[InteractiveInfo, InteractiveInfo]> = [];
  if (plateInfo && leverInfo) connections.push([plateInfo, leverInfo]);
  // Show the actual local mechanism. Unrelated stations do not share a
  // physical wire; their former cross-level threads cut through the cats.
  auraSight.registerConnections(connections);

  // ─── Level state (shell only) ───────────────────────────────────

  let elderEncounterDone = false;
  let levelComplete = false;

  // ─── Per-puzzle guidance (objective line + anchored cue chip) ─────
  //
  // Each puzzle's getCue() names its current step; the driver shows the
  // first unsolved puzzle's cue pinned to the thing to act on, with an
  // "(N/5)" objective line at the top. See LevelCueDriver.
  const cueDriver = new LevelCueDriver(
    [
      { puzzle: puzzles[0], objective: "Lift the seized crane" },
      { puzzle: puzzles[1], objective: "Feed the crows" },
      { puzzle: puzzles[2], objective: "Corral the kittens" },
      { puzzle: puzzles[3], objective: "Solve the hoodie jigsaw" },
      { puzzle: puzzles[4], objective: "Shatter the rusted blockage" },
    ],
    gameEvents,
  ).attach(scene);
  base.addDisposeCallback(() => cueDriver.dispose());

  // ─── Blockage shattered triggers the Elder Kitty cutscene ─────────
  base.addUnsub(gameEvents.on(GameEvents.BLOCKAGE_SHATTERED, (data) => {
    if (data?.key !== "finalBlockage") return;
    setTimeout(() => startElderEncounter(), 1500);
  }));

  // ─── Portal entry (guard: Elder cutscene already emits LEVEL_COMPLETE) ──
  base.addUnsub(gameEvents.on(GameEvents.PORTAL_ENTERED, () => {
    if (levelComplete) return;
    levelComplete = true;
    gameEvents.emit(GameEvents.LEVEL_COMPLETE, { level: "RedwoodScrapyard" });
  }));

  function startElderEncounter(): void {
    if (elderEncounterDone) return;
    elderEncounterDone = true;

    // Broadcast the encounter moment for QA telemetry + AudioManager.
    // L2 and L3 emit the same event at the start of their Elder cutscenes;
    // L1 was the last scene where this was missing, which made "player met
    // the Elder" invisible to the recorder.
    gameEvents.emit(GameEvents.ELDER_ENCOUNTER, {
      elder: "kitty",
      level: "RedwoodScrapyard",
    });

    const elderPos = elderKittyPos;

    // Build Elder Kitty cutscene from dialogue data
    const dialogueSteps: CutsceneStep[] = ELDER_KITTY_ENCOUNTER.map((line): CutsceneStep => ({
      type: "dialogue",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    }));

    const steps: CutsceneStep[] = [
      // Canon reveal: per book p. 14, the scrap visibly "falls away
      // into a starry void" and Kitty appears at the far edge of that
      // void. We fade to black, reveal the Elder off-screen, then run
      // the `reveal` step which paints the scrap-into-stars curtain
      // via ElderRevealPostProcess (shader + particle emitters). The
      // final fade-in lands on the Elder as the void clears.
      { type: "fade", fadeDirection: "out", duration: 1200 },
      { type: "wait", duration: 400 },
      { type: "callback", fn: () => {
        // Reveal the Elder now that the screen is black.
        elderKitty.mesh.setEnabled(true);
        // Boost the beacon briefly so the fade-in lands on him as a
        // single cool glow in the void before the starry backdrop
        // resolves back to the scrapyard art.
        elderBeacon.intensity = 1.6;
      }},
      { type: "reveal", preset: "starry-void", duration: 2500 },
      { type: "fade", fadeDirection: "in", duration: 1200 },
      { type: "pan", target: { x: elderPos.x, y: elderPos.y }, duration: 2000 },
      { type: "wait", duration: 500 },
      ...dialogueSteps,
      { type: "shake", duration: 500, intensity: 0.008 },
      { type: "callback", fn: () => {
        gameEvents.emit(GameEvents.COOP_ABILITY_UNLOCKED, {
          abilityId: "resonantDrop",
          abilityName: "Resonant Drop",
        });
      }},
      { type: "wait", duration: 1000 },
      { type: "fade", fadeDirection: "out", duration: 1500 },
      { type: "callback", fn: () => {
        levelComplete = true;
        gameEvents.emit(GameEvents.LEVEL_COMPLETE, { level: "RedwoodScrapyard" });
        exitPortal.open();
      }},
    ];

    cutsceneSystem.play(steps);
  }

  // ─── Dispose puzzles on scene teardown ──────────────────────────
  base.addDisposeCallback(() => {
    for (const puzzle of puzzles) {
      try { puzzle.dispose(); } catch (e) { console.warn("Puzzle dispose error:", e); }
    }
  });

  // ─── Wounds and Medicine: Grease Caches ─────────────────────────
  // Spec §4 "Secondary: Grease Caches" — Hollow-Knight-bench cadence,
  // placed between the harder puzzles so a wounded Basil has a safety
  // net without having to backtrack. Section spans:
  //   Section 2 (30-70): mid-level — between Crow-Squirrel and Kitten
  //   Section 3 (70-110): pre-apex
  //   Section 4 (110-135): pre-Final Blockage, tucked at the cliff base
  const greaseCaches = [
    new GreaseCache(scene, {
      key: "greaseCache_L1_s2",
      position: new Vector3(48, 1, 0),
      stationFloorY: REDWOOD_SCRAPYARD_PLATFORMS.groundTopY,
      shadowGenerator: shadowGen,
      label: "Grease Cache",
    }),
    new GreaseCache(scene, {
      key: "greaseCache_L1_s3",
      position: new Vector3(82, 1, 0),
      stationFloorY: REDWOOD_SCRAPYARD_PLATFORMS.groundTopY,
      shadowGenerator: shadowGen,
      label: "Grease Cache",
    }),
    new GreaseCache(scene, {
      key: "greaseCache_L1_s4",
      position: new Vector3(122, 1, 0),
      stationFloorY: REDWOOD_SCRAPYARD_PLATFORMS.groundTopY,
      shadowGenerator: shadowGen,
      label: "Grease Cache",
    }),
  ];
  base.addDisposeCallback(() => {
    for (const cache of greaseCaches) {
      try { cache.dispose(); } catch (e) { console.warn("GreaseCache dispose error:", e); }
    }
  });

  // ─── Game Loop ──────────────────────────────────────────────────

  base.registerGameLoop(
    (dt, sceneTime) => {
      // Tick each puzzle and refresh its entry in levelState so
      // finalBlockage's gating always sees fresh solved flags.
      for (const puzzle of puzzles) {
        puzzle.update(dt, sceneTime);
        levelState.set(puzzle.id, puzzle.getState());
      }

      // Wounds/Medicine: tick every grease cache so the respawn timer
      // advances and the brass-lid pulse breathes on ready caches.
      for (const cache of greaseCaches) {
        cache.tick(dt);
      }

      // Portal entry is tracked once the blockage has been shattered
      // (Elder cutscene opens the portal at its end).
      if (finalBlockage.getState().solved) {
        exitPortal.checkEntry(minna.mesh.position, basil.mesh.position);
      }

      // Hoodie Papoose — keeps carried kittens locked to `calmed` so a
      // mid-carry purr timeout can't flip them back to frantic. No-op
      // until the jigsaw activates the carrier.
      papooseCarrier.update();

      // Co-op ability update
      resonantDrop.update(dt);

      // Making Biscuits: tick the minigame + proximity prompt for the
      // Kitty bedroll anchor.
      kittyBiscuit.update(dt);
    },
    {
      isLevelComplete: () => levelComplete,
      onAbilityKey: (key, pressed, released) => {
        // Biscuits (B / A / D) take priority when a session is active
        // so the paw taps don't race with movement or puzzle binds.
        if (kittyBiscuit.onAbilityKey(key, pressed, released)) {
          return true;
        }

        // Give each puzzle first crack at the key (e.g. crane's 1/2 binds)
        for (const puzzle of puzzles) {
          if (puzzle.onAbilityKey && puzzle.onAbilityKey(key, pressed, released)) {
            return true;
          }
        }

        // Q = Aura Sight toggle (override to also hide the jigsaw pattern)
        if (key === "q" && pressed) {
          if (auraSight.isActive) {
            minnaAbilities.deactivateAbility("auraSight");
            hoodieJigsaw.hidePattern();
          } else {
            minnaAbilities.activateAbility("auraSight");
          }
          return true;
        }

        // E = Minna context-sensitive: drop carried food, OR pick up nearby
        // food, OR toggle Shadow Slip. The drop-first ordering matters —
        // without it, pressing E while carrying food silently activated
        // Shadow Slip mid-delivery (footgun flagged in the L1 QA pass).
        if (key === "e" && pressed) {
          const carried = crowSquirrel.carriedBy(minna.mesh);
          if (carried) {
            carried.drop();
            return true;
          }
          const foodPickedUp = crowSquirrel.tryPickupFood(minna.mesh);
          if (!foodPickedUp) {
            if (shadowSlip.isActive) {
              minnaAbilities.deactivateAbility("shadowSlip");
            } else {
              minnaAbilities.activateAbility("shadowSlip");
            }
          }
          return true;
        }

        // , = Basil interact. Precedence:
        //   1) Grease Cache pickup (Wounds/Medicine — low-priority so
        //      it doesn't swallow puzzle interactions; only activates
        //      when Basil is actually in radius AND the harness needs
        //      healing)
        //   2) Jigsaw cycle nearest slot
        //   3) Food pickup
        if (key === "," && pressed) {
          for (const cache of greaseCaches) {
            if (cache.isReady && cache.isInInteractRange(basil.mesh.position)) {
              if (cache.consume(base.harnessWounds, basil.mesh.position)) {
                return true;
              }
            }
          }
          if (!hoodieJigsaw.tryCycleNearestSlot()) {
            crowSquirrel.tryPickupFood(basil.mesh);
          }
          return true;
        }

        // Co-op: F + M = Resonant Drop
        if (key === "f+m" && pressed) {
          tryCoopAbility(resonantDrop, "resonantDrop");
          return true;
        }

        return false;
      },
    },
  );

  // --- Start ambient audio ---
  setTimeout(() => audioManager.startAmbient(), 500);

  // --- Emit level started ---
  gameEvents.emit(GameEvents.LEVEL_STARTED, "RedwoodScrapyard");

  // --- Level intro cutscene with control hints ---
  const _introLines = LEVEL_INTRO["RedwoodScrapyard"] ?? [];
  const _introSteps: CutsceneStep[] = [
    { type: "fade", fadeDirection: "in", duration: 1200, color: { r: 0, g: 0, b: 0 } },
    { type: "wait", duration: 300 },
    // Push east to show the seized crane silhouette before handing control
    // back to the player — establishes direction of travel as "go right".
    { type: "pan", target: { x: 20, y: 4 }, duration: 1800, ease: "cubic" },
    { type: "wait", duration: 400 },
    ..._introLines.map((line): CutsceneStep => ({
      type: "dialogue",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    })),
    // Return framing to the cats before releasing control.
    { type: "pan", target: { x: 8, y: 3 }, duration: 900, ease: "cubic" },
    { type: "dialogue", speaker: "", text: controlHintForMode(inputManager.soloMode), duration: 3500 },
    { type: "wait", duration: 300 },
  ];
  // `playDeferred` (not raw setTimeout) so that `__startAtPuzzle` can cancel
  // this pending intro before the timer fires — see
  // docs/qa/analysis/startatpuzzle-audit-2026-04-18.md.
  cutsceneSystem.playDeferred(_introSteps, 600);

  return {
    physicsSystem,
    cameraSystem,
    inputManager,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    audioManager,
    cutsceneSystem,
  };
}

// ─── Environment Helpers ──────────────────────────────────────────────

/**
 * Creates a procedural background with gradient sky and
 * redwood trunk silhouettes for depth.
 */
function createAtmosphereMotes(scene: Scene): ParticleSystem {
  const W = SCRAPYARD.WORLD_WIDTH;
  const H = SCRAPYARD.WORLD_HEIGHT;

  // Phase C4 densified: 400 → 900 max particles, 50 → 120 emit rate.
  // Larger size variance + broader z-spread so the motes fill the whole
  // depth-band rather than sitting in a single midground plane.
  const ps = new ParticleSystem("redwoodMotes", 200, scene);
  ps.emitter = new Vector3(W / 2, H / 3, 0);
  ps.minEmitBox = new Vector3(-W / 2, -4, -4);
  ps.maxEmitBox = new Vector3(W / 2, 6, 3);
  ps.minLifeTime = 8;
  ps.maxLifeTime = 16;
  ps.minSize = 0.01;
  ps.maxSize = 0.05;
  ps.emitRate = 30;
  ps.minEmitPower = 0.05;
  ps.maxEmitPower = 0.35;
  ps.updateSpeed = 0.02;
  ps.gravity = new Vector3(0, 0.08, 0); // drifts up very slowly
  ps.direction1 = new Vector3(-0.1, 0.2, 0);
  ps.direction2 = new Vector3(0.1, 0.5, 0);
  ps.color1 = new Color4(0.0, 1.0, 0.53, 0.3);
  ps.color2 = new Color4(0.8, 0.9, 0.4, 0.15);
  ps.colorDead = new Color4(0.2, 0.4, 0.1, 0);
  ps.blendMode = ParticleSystem.BLENDMODE_ADD;
  ps.start();
  return ps;
}

/**
 * Creates invisible shadow zone trigger volumes for Shadow Slip.
 */
function createShadowZones(scene: Scene): ShadowZoneInfo[] {
  const zones: ShadowZoneInfo[] = [];

  const zoneDefs = [
    { position: new Vector3(12, 1.5, 0), width: 5, height: 1.5, depth: 1.5 },
    { position: new Vector3(38, 2.0, 0), width: 6, height: 1.2, depth: 1.5 },
    { position: new Vector3(65, 1.8, 0), width: 5, height: 1.5, depth: 1.5 },
    { position: new Vector3(88, 2.5, 0), width: 7, height: 1.3, depth: 1.5 },
    { position: new Vector3(115, 2.0, 0), width: 5, height: 1.5, depth: 1.5 },
    { position: new Vector3(138, 1.5, 0), width: 6, height: 1.2, depth: 1.5 },
  ];

  for (let i = 0; i < zoneDefs.length; i++) {
    const def = zoneDefs[i];
    const mesh = MeshBuilder.CreateBox(
      `shadowZone_${i}`,
      { width: def.width, height: def.height, depth: def.depth },
      scene,
    );
    mesh.position = def.position;
    mesh.isVisible = false;
    mesh.isPickable = false;

    // Semi-transparent dark visual
    const debugVisual = MeshBuilder.CreateBox(
      `shadowZoneVisual_${i}`,
      { width: def.width, height: def.height, depth: def.depth },
      scene,
    );
    debugVisual.position = def.position;
    debugVisual.isVisible = WORLD_DEBUG.SHOW_SHADOW_ZONE_VISUALS;
    debugVisual.isPickable = false;
    const darkMat = new StandardMaterial(`shadowZoneMat_${i}`, scene);
    darkMat.diffuseColor = new Color3(0.02, 0.01, 0.04);
    darkMat.emissiveColor = new Color3(0.01, 0.005, 0.02);
    darkMat.alpha = 0.35;
    debugVisual.material = darkMat;

    zones.push({ mesh });
  }

  return zones;
}

/**
 * Creates the ground plane and platforming surfaces.
 */
function createGroundAndPlatforms(
  scene: Scene,
  physicsSystem: PhysicsSystem,
  shadowGen: ShadowGenerator,
): { ground: Mesh | null; platforms: Mesh[] } {
  const W = SCRAPYARD.WORLD_WIDTH;

  // Ground
  const ground = physicsSystem.createGround(W, SCRAPYARD.GROUND_DEPTH, W / 2);
  const groundMat = new PBRMaterial("groundMat", scene);
  groundMat.albedoColor = new Color3(0.12, 0.09, 0.07);
  groundMat.metallic = 0.05;
  groundMat.roughness = 0.95;
  groundMat.emissiveColor = new Color3(0.01, 0.005, 0.01);
  // C6: noisy loam-floor bump so the ground reads as soil, not a flat
  // chocolate-brown plane. Tiled 30× because the scrapyard is 154 units
  // wide — one large stamp would smear.
  const groundNoise = getOrMakeNoiseBump(scene, "scrapyard_loam", 0.7);
  groundNoise.uScale = 30;
  groundNoise.vScale = 3;
  groundMat.bumpTexture = groundNoise;
  groundMat.bumpTexture.level = 0.5;
  if (ground) {
    ground.material = groundMat;
    ground.receiveShadows = true;
  }

  // Platform material: weathered wood with a warm amber emissive rim so it
  // reads as walkable through fog and against the brown forest dressing.
  // Rusted brass + weathered wood look for the platform staircase.
  // Gemini QA flagged the original flat brown as "untextured prototype."
  const platformMat = new PBRMaterial("platformMat", scene);
  platformMat.albedoColor = new Color3(0.55, 0.38, 0.18);  // warm wood
  platformMat.metallic = 0.35;                               // rusted metal sheen
  platformMat.roughness = 0.65;
  platformMat.emissiveColor = new Color3(0.08, 0.04, 0.02); // subtle warmth
  // C6: wood-grain bump on the planks. Tiled 4× per platform width to
  // give short plank-sized highlights rather than a single long fiber.
  const plankNoise = getOrMakeNoiseBump(scene, "scrapyard_plank", 0.45);
  plankNoise.uScale = 4;
  plankNoise.vScale = 1;
  platformMat.bumpTexture = plankNoise;
  platformMat.bumpTexture.level = 0.3;


  const platforms: Mesh[] = [];
  // Platform geometry lives in src-babylon/levels/layouts/ (reachability lint).
  for (const def of REDWOOD_SCRAPYARD_PLATFORMS.platforms) {
    const plat = physicsSystem.createPlatform(
      def.name,
      def.w,
      platformHeight(def),
      platformDepth(def),
      new Vector3(def.x, def.y, 0),
    );
    plat.material = platformMat;
    plat.receiveShadows = true;
    shadowGen.addShadowCaster(plat);
    platforms.push(plat);
  }

  return { ground, platforms };
}
